import { supabaseClient } from '../lib/supabaseClient';

export type ReportRole =
  | 'manager'
  | 'life_consultant'
  | 'nurse'
  | 'care'
  | 'plan_creator'
  | 'nutritionist'
  | 'cook'
  | 'other';

export type ReportWorkMode = 'dedicated' | 'concurrent';

export interface EmployeeReportAssignment {
  id: string;
  employeeId: string;
  reportRole: ReportRole;
  workMode: ReportWorkMode;
  allocationRatio: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
}

export const REPORT_ROLE_LABELS: Record<ReportRole, string> = {
  manager: '管理者',
  life_consultant: '生活相談員',
  nurse: '看護職員',
  care: '介護職員',
  plan_creator: '計画作成担当者',
  nutritionist: '栄養士',
  cook: '調理師',
  other: 'その他従業員'
};

export const REPORT_ROLE_TO_SUMMARY_CATEGORY: Record<ReportRole, string> = {
  manager: 'その他従業員',
  life_consultant: '生活相談員',
  nurse: '看護職員',
  care: '介護職員',
  plan_creator: '計画作成担当者',
  nutritionist: '栄養士',
  cook: '調理師',
  other: 'その他従業員'
};

type AssignmentRow = {
  id: string | number;
  employee_id: string | number;
  report_role: ReportRole;
  work_mode: ReportWorkMode;
  allocation_ratio: string | number | null;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
};

const normalizeAssignment = (row: AssignmentRow): EmployeeReportAssignment => ({
  id: String(row.id),
  employeeId: String(row.employee_id),
  reportRole: row.report_role,
  workMode: row.work_mode,
  allocationRatio:
    row.allocation_ratio === null || row.allocation_ratio === undefined
      ? null
      : Number(row.allocation_ratio),
  effectiveFrom: row.effective_from,
  effectiveTo: row.effective_to,
  notes: row.notes
});

export const isAssignmentActiveInPeriod = (
  assignment: EmployeeReportAssignment,
  periodStart: string,
  periodEnd: string
): boolean =>
  assignment.effectiveFrom <= periodEnd &&
  (!assignment.effectiveTo || assignment.effectiveTo >= periodStart);

export const isAssignmentActiveOnDate = (
  assignment: EmployeeReportAssignment,
  date: string
): boolean =>
  assignment.effectiveFrom <= date &&
  (!assignment.effectiveTo || assignment.effectiveTo >= date);

export async function fetchEmployeeReportAssignments(
  periodStart: string,
  periodEnd: string
): Promise<EmployeeReportAssignment[]> {
  if (!supabaseClient) {
    throw new Error('Supabaseの設定が見つかりません。');
  }

  const { data, error } = await supabaseClient
    .from('employee_report_assignments')
    .select(
      'id,employee_id,report_role,work_mode,allocation_ratio,effective_from,effective_to,notes'
    )
    .lte('effective_from', periodEnd)
    .or(`effective_to.is.null,effective_to.gte.${periodStart}`)
    .order('employee_id')
    .order('report_role');

  if (error) throw error;
  return ((data ?? []) as AssignmentRow[]).map(normalizeAssignment);
}

export const formatAssignmentRatio = (assignment: EmployeeReportAssignment): string => {
  if (assignment.allocationRatio === null) return '';
  const minutes = Math.round(assignment.allocationRatio * 8 * 60);
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours}時間` : `${hours}時間${remainder}分`;
};
